// material-ui
import { useTheme, styled } from '@mui/material/styles';
import { Avatar, Box, InputAdornment, OutlinedInput, Popper } from '@mui/material';

// third-party
import { bindToggle } from 'material-ui-popup-state';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

// project imports

// assets
import { IconAdjustmentsHorizontal, IconSearch, IconX } from '@tabler/icons';
import { shouldForwardProp } from '@mui/system';
import { useDispatch } from 'store';
import { clearState } from 'store/slices/stateMachines';

// styles
const PopperStyle = styled(Popper, { shouldForwardProp })(({ theme }) => ({
    zIndex: 1100,
    width: '99%',
    top: '-55px !important',
    padding: '0 12px',
    [theme.breakpoints.down('sm')]: {
        padding: '0 10px'
    }
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 434,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important'
    },
    [theme.breakpoints.down('lg')]: {
        width: 250
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 4,
        background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff'
    }
}));

const HeaderAvatarStyle = styled(Avatar, { shouldForwardProp })(({ theme }) => ({
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
    color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
    '&:hover': {
        background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
        color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
    }
}));

interface Props {
    value: string;
    setValue: (value: string) => void;
    popupState: any;
}

// ==============================|| SEARCH INPUT - MOBILE||============================== //

const MobileSearch = ({ value, setValue, popupState }: Props) => {
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (value && value.length > 3) setSearchParams({ ...searchParams, search: value });
    }, [searchParams, setSearchParams, value]);

    return (
        <OutlineInputStyle
            id="input-search-header"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search"
            startAdornment={
                <InputAdornment position="start">
                    <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                </InputAdornment>
            }
            endAdornment={
                <InputAdornment
                    position="end"
                    onClick={() => {
                        setValue('');
                        searchParams.delete('search');
                        setSearchParams(searchParams);
                    }}
                >
                    <HeaderAvatarStyle variant="rounded">
                        <IconX stroke={1.5} size="20px" />
                    </HeaderAvatarStyle>
                </InputAdornment>
            }
            aria-describedby="search-helper-text"
            inputProps={{ 'aria-label': 'weight' }}
        />
    );
};

// ==============================|| SEARCH INPUT ||============================== //

const SearchSection = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    const [value, setValue] = useState('');
    const navigate = useNavigate();

    const { pathname } = useLocation();

    useEffect(() => {
        if (pathname?.includes('process')) return;
        if (!value) {
            dispatch(clearState([]));
            searchParams.delete('search');
            setSearchParams(searchParams);
        }
        if (value && value.length > 3) {
            dispatch(clearState([]));
            setSearchParams({ ...searchParams, search: value });
        }
    }, [searchParams, setSearchParams, value]);

    useEffect(() => {
        setValue('');
        dispatch(clearState([]));
    }, [pathname]);

    return (
        <>
            {/* <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <PopupState variant="popper" popupId="demo-popup-popper">
                    {(popupState) => (
                        <>
                            <Box sx={{ ml: 2 }}>
                                <HeaderAvatarStyle variant="rounded" {...bindToggle(popupState)}>
                                    <IconSearch stroke={1.5} size="1.2rem" />
                                </HeaderAvatarStyle>
                            </Box>
                            <PopperStyle {...bindPopper(popupState)} transition>
                                {({ TransitionProps }) => (
                                    <>
                                        <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                                            <Card
                                                sx={{
                                                    background: theme.palette.mode === 'dark' ? theme.palette.dark[900] : '#fff',
                                                    [theme.breakpoints.down('sm')]: {
                                                        border: 0,
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ p: 2 }}>
                                                    <Grid container alignItems="center" justifyContent="space-between">
                                                        <Grid item xs>
                                                            <MobileSearch value={value} setValue={setValue} popupState={popupState} />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Card>
                                        </Transitions>
                                    </>
                                )}
                            </PopperStyle>
                        </>
                    )}
                </PopupState>
            </Box> */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    onKeyDown={(e) => {
                        pathname?.includes('process') &&
                            e.key === 'Enter' &&
                            value !== '' &&
                            navigate(`/search/${value}`, { replace: true });
                    }}
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    endAdornment={
                        value && (
                            <InputAdornment position="end" onClick={() => setValue('')}>
                                <Box sx={{ ml: 2 }}>
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            ...theme.typography.commonAvatar,
                                            ...theme.typography.mediumAvatar,
                                            background:
                                                theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.orange.light,
                                            color: theme.palette.orange.dark,
                                            '&:hover': {
                                                background: theme.palette.orange.dark,
                                                color: theme.palette.orange.light
                                            }
                                        }}
                                    >
                                        <IconX stroke={1.5} size="1.3rem" />
                                    </Avatar>
                                </Box>
                            </InputAdornment>
                        )
                    }
                />
            </Box>
        </>
    );
};

export default SearchSection;
